/**
 * Normalize committee sheet data to handle various spreadsheet formats:
 * - Merged cells (value only in first row, rest empty)
 * - One member per row with committee name fill-down
 * - Members spread across multiple columns (starting from mapped column)
 * - Aggregates rows with same committee name into single row with members array
 */

export enum CommitteeSheetFormat {
  Standard = 'standard',
  OnePerRow = 'onePerRow',
  MultiColumn = 'multiColumn'
}

interface RawRow {
  [key: string]: unknown
}

interface NormalizedCommittee {
  name: string
  members: string[]
  [key: string]: unknown
}

/**
 * Check if a row has any meaningful data
 */
function rowHasData (row: RawRow, excludeKeys: string[] = []): boolean {
  return Object.entries(row).some(([key, value]) => {
    if (excludeKeys.includes(key)) {
      return false
    }
    return value !== undefined && value !== null && String(value).trim() !== ''
  })
}

/**
 * Get the value of the first non-empty "member" field from a row
 */
function getMemberValue (row: RawRow, memberField: string): string | undefined {
  const value = row[memberField]
  if (value !== undefined && value !== null && String(value).trim() !== '') {
    return String(value).trim()
  }
  return undefined
}

/**
 * Get columns starting from a specific column and all columns after it
 */
function getColumnsFromIndex (allColumns: string[], startColumn: string): string[] {
  const startIndex = allColumns.indexOf(startColumn)
  if (startIndex === -1) {
    return []
  }
  return allColumns.slice(startIndex)
}

/**
 * Collect all member values from multiple columns starting at memberField
 */
function collectMembersFromColumnsOnward (row: RawRow, memberColumns: string[]): string[] {
  const members: string[] = []
  for (const col of memberColumns) {
    const value = row[col]
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      members.push(String(value).trim())
    }
  }
  return members
}

/**
 * Get all unique column names across all rows
 */
function getAllColumns (rows: RawRow[], providedColumns?: string[]): string[] {
  if (providedColumns && providedColumns.length > 0) {
    return providedColumns
  }
  const columnSet = new Set<string>()
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      columnSet.add(key)
    }
  }
  return Array.from(columnSet)
}

/**
 * Check if there are multiple columns with data after the member column
 */
export function hasMultipleColumnsAfterMember (
  rows: RawRow[],
  memberField: string,
  providedColumns?: string[]
): boolean {
  if (!rows || rows.length === 0) {
    return false
  }

  const allColumns = getAllColumns(rows, providedColumns)
  const memberColumns = getColumnsFromIndex(allColumns, memberField)

  // Check if any row has data in more than one member column
  for (const row of rows) {
    let columnsWithData = 0
    for (const col of memberColumns) {
      const value = row[col]
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        columnsWithData++
        if (columnsWithData > 1) {
          return true
        }
      }
    }
  }

  return false
}

/**
 * Normalize multi-column committee format where members are spread across
 * multiple columns starting from the mapped member column
 */
export function normalizeMultiColumnCommitteeSheet (
  rows: RawRow[],
  nameField: string,
  memberField: string,
  providedColumns?: string[]
): NormalizedCommittee[] {
  if (!rows || rows.length === 0) {
    return []
  }

  const allColumns = getAllColumns(rows, providedColumns)
  const memberColumns = getColumnsFromIndex(allColumns, memberField)

  return rows
    .filter(row => {
      const nameValue = row[nameField]
      return nameValue !== undefined && nameValue !== null && String(nameValue).trim() !== ''
    })
    .map(row => {
      const members = collectMembersFromColumnsOnward(row, memberColumns)

      // Copy other fields (exclude name and member columns)
      const otherFields: RawRow = {}
      for (const [key, value] of Object.entries(row)) {
        if (key !== nameField && !memberColumns.includes(key)) {
          otherFields[key] = value
        }
      }

      return {
        ...otherFields,
        name: String(row[nameField]).trim(),
        members
      }
    })
}

/**
 * Normalize committee sheet data by:
 * 1. Filling down committee names from merged cells
 * 2. Filtering out completely empty rows
 * 3. Aggregating members by committee name
 *
 * @param rows - Raw parsed spreadsheet rows
 * @param nameField - The field key for committee name (after mapping)
 * @param memberField - The field key for member name (after mapping)
 * @returns Normalized rows with one entry per committee and members as array
 */
export function normalizeCommitteeSheet (
  rows: RawRow[],
  nameField: string = 'name',
  memberField: string = 'members'
): NormalizedCommittee[] {
  if (!rows || rows.length === 0) {
    return []
  }

  // Step 1: Fill down committee names and filter empty rows
  let lastCommitteeName: string | undefined
  const filledRows: Array<{ name: string, member: string | undefined, originalRow: RawRow }> = []

  for (const row of rows) {
    const nameValue = row[nameField]
    const name = nameValue !== undefined && nameValue !== null ? String(nameValue).trim() : ''
    const member = getMemberValue(row, memberField)

    // Check if row has any data (excluding name field to detect member-only rows)
    const hasOtherData = rowHasData(row, [nameField])

    // Completely empty row - gap between committees, reset
    if (!name && !hasOtherData) {
      lastCommitteeName = undefined
      continue
    }

    // Row has a committee name - update the current committee
    if (name) {
      lastCommitteeName = name
    }

    // Skip rows with no committee context
    if (!lastCommitteeName) {
      continue
    }

    // Add row with filled committee name
    filledRows.push({
      name: lastCommitteeName,
      member,
      originalRow: row
    })
  }

  // Step 2: Aggregate by committee name
  const committeeMap = new Map<string, NormalizedCommittee>()

  for (const { name, member, originalRow } of filledRows) {
    const existing = committeeMap.get(name)

    if (existing) {
      // Add member to existing committee
      if (member) {
        existing.members.push(member)
      }
    } else {
      // Create new committee entry
      // Copy any other fields from the first row (e.g., committee type)
      const otherFields: RawRow = {}
      for (const [key, value] of Object.entries(originalRow)) {
        if (key !== nameField && key !== memberField) {
          otherFields[key] = value
        }
      }

      committeeMap.set(name, {
        ...otherFields,
        name,
        members: member ? [member] : []
      })
    }
  }

  return Array.from(committeeMap.values())
}

/**
 * Detect the committee sheet format:
 * - MultiColumn: has data in multiple columns starting from the member field
 * - OnePerRow: multiple rows share the same committee name or have empty names
 * - Standard: one row per committee with members in a single column
 */
export function detectCommitteeSheetFormat (
  rows: RawRow[],
  nameField: string = 'name',
  memberField?: string,
  providedColumns?: string[]
): CommitteeSheetFormat {
  if (!rows || rows.length === 0) {
    return CommitteeSheetFormat.Standard
  }

  if (memberField && hasMultipleColumnsAfterMember(rows, memberField, providedColumns)) {
    return CommitteeSheetFormat.MultiColumn
  }

  const committeeNames: string[] = []
  let hasEmptyNames = false

  for (const row of rows) {
    const nameValue = row[nameField]
    const name = nameValue !== undefined && nameValue !== null ? String(nameValue).trim() : ''

    // Check if row has other data (member data)
    const hasOtherData = rowHasData(row, [nameField])

    if (!name && hasOtherData) {
      // Row has data but no committee name - indicates fill-down format
      hasEmptyNames = true
    } else if (name) {
      committeeNames.push(name)
    }
  }

  // If there are empty names with data, it's one-per-row format
  if (hasEmptyNames) {
    return CommitteeSheetFormat.OnePerRow
  }

  // If same committee name appears multiple times, it's one-per-row format
  const uniqueNames = new Set(committeeNames)
  if (uniqueNames.size < committeeNames.length) {
    return CommitteeSheetFormat.OnePerRow
  }

  return CommitteeSheetFormat.Standard
}
